import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Settings, Globe, Server, ShieldCheck, Database, Save, RefreshCw, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import api from '../../lib/api';
import { toast } from 'sonner';

const AdminSettings = () => {
    const [config, setConfig] = useState({
        frontend_url: '',
        backend_url: '',
        maintenance_mode: false,
        public_registrations: true,
        whatsapp_enabled: true
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        try {
            const response = await api.get('/admin/config');
            setConfig(response.data);
        } catch (error) {
            toast.error('Error al cargar configuración');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            await api.post('/admin/config', config);
            toast.success('Configuración guardada correctamente');
        } catch (error) {
            toast.error('Error al guardar cambios');
        }
    };

    return (
        <AdminLayout>
            <div className="max-w-4xl space-y-8">
                <section className="grid gap-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center">
                                <Globe className="mr-2 h-5 w-5 text-wedding-primary" />
                                URLs del Sistema
                            </CardTitle>
                            <CardDescription>Configura los dominios base para la comunicación del sistema.</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid gap-2">
                                <Label htmlFor="frontend">Frontend URL</Label>
                                <Input
                                    id="frontend"
                                    value={config.frontend_url}
                                    onChange={e => setConfig({ ...config, frontend_url: e.target.value })}
                                    placeholder="https://bodas.lovable.app"
                                />
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="backend">Backend API URL</Label>
                                <Input
                                    id="backend"
                                    value={config.backend_url}
                                    onChange={e => setConfig({ ...config, backend_url: e.target.value })}
                                    placeholder="https://api.bodas.lovable.app"
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center">
                                <ShieldCheck className="mr-2 h-5 w-5 text-wedding-primary" />
                                Seguridad y Estado
                            </CardTitle>
                            <CardDescription>Controla el acceso global y el modo de operación.</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                                <div className="space-y-0.5">
                                    <Label className="text-base">Modo Mantenimiento</Label>
                                    <p className="text-sm text-muted-foreground italic">Desactiva el acceso a todos los usuarios excepto admins.</p>
                                </div>
                                <Switch
                                    checked={config.maintenance_mode}
                                    onCheckedChange={checked => setConfig({ ...config, maintenance_mode: checked })}
                                />
                            </div>

                            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                                <div className="space-y-0.5">
                                    <Label className="text-base">Registros Públicos</Label>
                                    <p className="text-sm text-muted-foreground">Permite que nuevos novios se registren desde la landing.</p>
                                </div>
                                <Switch
                                    checked={config.public_registrations}
                                    onCheckedChange={checked => setConfig({ ...config, public_registrations: checked })}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-red-200 bg-red-50/20">
                        <CardHeader>
                            <CardTitle className="text-red-800 flex items-center">
                                <AlertTriangle className="mr-2 h-5 w-5" />
                                Zona de Peligro
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="flex gap-4">
                            <Button variant="outline" className="border-red-200 text-red-700 hover:bg-red-100">
                                Limpiar Cache del Sistema
                            </Button>
                            <Button variant="outline" className="border-red-200 text-red-700 hover:bg-red-100">
                                Reiniciar Contadores de Uso
                            </Button>
                        </CardContent>
                    </Card>
                </section>

                <div className="flex justify-end sticky bottom-0 bg-slate-50 py-4 border-t gap-3">
                    <Button variant="outline" onClick={fetchConfig} disabled={loading}>
                        <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        Descartar Cambios
                    </Button>
                    <Button onClick={handleSave} className="bg-wedding-primary">
                        <Save className="mr-2 h-4 w-4" />
                        Guardar Configuración
                    </Button>
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminSettings;
